import { useState, useEffect, useCallback } from 'react';
import {
    sendBrazeCustomEvent,
    showOopsPage,
    updateCart,
    useAppshellMeta,
    useAppShellPush,
    useAppshellRouter,
    useFeatureToggle,
    useLogger,
    useUniversalFetchingHelpers,
} from '@ids/appshell';
import { useQuestionnairePageContext, useResetQuestionnaireEmbed } from '@ids/context/QuestionnairePage';
import useOverstepPage from '@ids/pages/overstep/useOverstepPage';
import { useIdsGaTracking, useQueryParams } from '@ids/hooks';
import { NowApiResponse } from '@ids/types/now-api';
import { Maybe } from '@ids/types/utility';

type SparrowResponse = {
    question: string;
    answer?: string;
    id: number;
};

export const isClassicSurvey = (name?: Maybe<string>) =>
    typeof name === 'string' && name.match(/(classic|klassik|normal)/i);

export const countRooms = (
    responses: SparrowResponse[] = [],
    shouldSplitRoomsOnKeyword = true,
    roomQuestionId?: number
) => {
    const roomAnswer = String(responses.find(({ id }) => id === roomQuestionId)?.answer || responses[0]?.answer || '');

    const rooms = new Set<string>();

    (shouldSplitRoomsOnKeyword ? roomAnswer.split(/, |\b(?:en|et|und|and)\b/i) : [roomAnswer]).forEach(room =>
        rooms.add(room.trim().toLowerCase())
    );

    return rooms.size;
};

const useSurveySparrowMessage = (surveySparrowIframeElement: HTMLIFrameElement | null) => {
    const [isFormLoading, setIsFormLoading] = useState(true);
    const logger = useLogger();
    const { isApp } = useAppshellMeta();
    const { routes } = useAppshellRouter();
    const appshellPush = useAppShellPush();
    const { trackIdsEvent } = useIdsGaTracking();
    const { shopBellaCart } = useFeatureToggle();
    const { get } = useUniversalFetchingHelpers();
    const { enableRedirectsToOverstep } = useOverstepPage();
    const questionnairePageData = useQuestionnairePageContext();
    const resetEmbedData = useResetQuestionnaireEmbed();
    const queryParams = useQueryParams();

    const getNativeCartRedirectUrl = useCallback(() => {
        const device = queryParams.get('device') || undefined;
        const layout = queryParams.get('layout') || undefined;
        const enableTracking = queryParams.get('enableTracking') === 'true';

        const queryParamsArray = [
            `ww_ft_shop_bella_cart=1`,
            device && `device=${device}`,
            layout && `layout=${layout}`,
            enableTracking && `enableTracking=${enableTracking}`,
        ];

        const queryParamsString = queryParamsArray.filter(Boolean).join('&');
        return `${routes.CART_NATIVE}?${queryParamsString}`;
    }, [queryParams, routes.CART_NATIVE]);

    const redirectToCart = useCallback(() => {
        appshellPush(getNativeCartRedirectUrl());
        if (shopBellaCart) {
            appshellPush(isApp ? getNativeCartRedirectUrl() : routes.CART);
        } else {
            appshellPush(routes.ALICE_CART);
        }
    }, [appshellPush, getNativeCartRedirectUrl, isApp, routes.ALICE_CART, routes.CART, shopBellaCart]);

    const addIdsProductsToCart = useCallback(
        async (allResponses: SparrowResponse[]) => {
            const roomCount = countRooms(
                allResponses,
                questionnairePageData?.splitRooms,
                questionnairePageData?.roomsCountQuestionId
            );

            try {
                const addToCartResponse = await get<NowApiResponse<unknown>>({
                    path: '/cart/add',
                    queryParams: {
                        quantity: roomCount,
                        simplesku: questionnairePageData?.sku,
                    },
                });

                if (!addToCartResponse.success) {
                    showOopsPage();
                    logger.error(`IDS SurveySparrow - onComplete error: Product was not successfully added to cart`);
                    return;
                }
            } catch (error) {
                showOopsPage();
                logger.error(`IDS SurveySparrow - ${error}`);
                return;
            }

            updateCart();
            redirectToCart();
        },
        [get, logger, questionnairePageData, redirectToCart]
    );

    const trackSurveySubmitted = useCallback(() => {
        logger.info('Studio Survey Sparrow submitted');

        trackIdsEvent({
            action: 'Questionnaire Interaction',
            label: `sparrow_${
                !isClassicSurvey(questionnairePageData?.formName) && questionnairePageData?.formName
                    ? `${questionnairePageData?.formName}_`
                    : ''
            }completed`,
        });
        sendBrazeCustomEvent({
            name: `idsQuestionnaireCompleted${
                questionnairePageData?.formName ? `_${questionnairePageData?.formName}` : ''
            }`,
        });
    }, [logger, questionnairePageData?.formName, trackIdsEvent]);

    const handleSurveySparrowMessage = useCallback(
        (event: MessageEvent) => {
            if (event?.data?.type === 'surveyLoadStarted') {
                setIsFormLoading(false);
                sendBrazeCustomEvent({
                    name: `idsQuestionnaireStarted${
                        questionnairePageData?.formName ? `_${questionnairePageData?.formName}` : ''
                    }`,
                });

                trackIdsEvent({
                    action: questionnairePageData?.formName ? 'Questionnaire opened' : 'Screen show',
                    label: `sparrow_${
                        !isClassicSurvey(questionnairePageData?.formName) && questionnairePageData?.formName
                            ? `${questionnairePageData?.formName}_`
                            : ''
                    }view`,
                });
            }

            if (event?.data?.type === 'surveyCompleted') {
                if (!questionnairePageData?.sku) {
                    return;
                }

                trackSurveySubmitted();

                addIdsProductsToCart(event.data?.response);

                enableRedirectsToOverstep(questionnairePageData.sku);

                resetEmbedData();

                if (surveySparrowIframeElement) {
                    /* eslint-disable no-param-reassign */
                    surveySparrowIframeElement.style.display = 'none';
                    surveySparrowIframeElement.style.visibility = 'hidden';
                    /* eslint-enable */
                }
            }
        },
        [
            addIdsProductsToCart,
            enableRedirectsToOverstep,
            questionnairePageData?.formName,
            questionnairePageData?.sku,
            resetEmbedData,
            surveySparrowIframeElement,
            trackIdsEvent,
            trackSurveySubmitted,
        ]
    );

    useEffect(() => {
        window.addEventListener('message', handleSurveySparrowMessage);

        return () => {
            window.removeEventListener('message', handleSurveySparrowMessage);
        };
    }, [handleSurveySparrowMessage]);

    return { isFormLoading };
};

export default useSurveySparrowMessage;
